import { useEffect, useRef } from 'react';

import { TUseComposerAutoFocusParams } from '@/components/molecule/ChatInput/types';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { consumeComposerFocusRequest, selectIsComposerFocusRequested } from '@/store/composerFocus/composerFocusSlice';
import { hasOpenDialog } from '@/utils/hasOpenDialog/hasOpenDialog';

/**
 * Decides when the cursor goes into the message input.
 * It also moves the cursor from the send button to the input when a send starts.
 */
const useComposerAutoFocus = ({ textAreaRef, sendButtonRef, isDisabled }: TUseComposerAutoFocusParams) => {
  const dispatch = useAppDispatch();
  const isFocusRequested = useAppSelector(selectIsComposerFocusRequested);
  const hasClaimedArrivalFocusRef = useRef(false);
  const wasBlockedByDialogRef = useRef(false);

  useEffect(() => {
    if (isDisabled) return;

    // INFO: Checks if any claim for focus is pendind
    const isClaimPending = () => !hasClaimedArrivalFocusRef.current || isFocusRequested;

    // INFO: Puts the cursor in the input if it is requested when allowed and returns whether it did.
    const claimFocus = (): boolean => {
      if (!isClaimPending()) return false;

      if (hasOpenDialog()) {
        wasBlockedByDialogRef.current = true;
        return false;
      }

      // INFO: On arrival(mount) the focus is taken when nothing else has it.
      // When a user clicks an element that triggers this request for focus, here move the natural focus from the element to the input

      const isFocusUnclaimed = document.activeElement === null || document.activeElement === document.body;
      if (!isFocusRequested && !isFocusUnclaimed) return false;

      hasClaimedArrivalFocusRef.current = true;
      textAreaRef.current?.focus();
      if (isFocusRequested) dispatch(consumeComposerFocusRequest());
      return true;
    };

    if (claimFocus() || !isClaimPending() || !wasBlockedByDialogRef.current) return;

    // INFO: Watches if dialogs from rio are on the page, wait for them to leave and restore focus to the input once they do.
    const observer = new MutationObserver(() => {
      if (claimFocus()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isDisabled, isFocusRequested, dispatch, textAreaRef]);

  useEffect(() => {
    // INFO: While the answer is in flight the send button stops working, so if the cursor is on it, it is moved to the input(where the user types next).
    if (!isDisabled) return;
    if (document.activeElement === sendButtonRef.current) textAreaRef.current?.focus();
  }, [isDisabled, textAreaRef, sendButtonRef]);
};

export { useComposerAutoFocus };
